import { useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useNavigate, useSearchParams } from 'router'
import { useUser } from 'store'

export const HARDWARE_VIEW_MODES = {
  SIMPLE: 'simple',
  COMPLETE: 'complete'
}

const HARDWARE_VIEW_MODE_STORAGE_KEY = 'supervision_hardware_view_mode'

export const canUseCompleteHardwareView = (user) => Boolean(user?.roles?.hyperis || user?.admin)

const normalizeViewMode = (view) =>
  view === HARDWARE_VIEW_MODES.COMPLETE ? HARDWARE_VIEW_MODES.COMPLETE : HARDWARE_VIEW_MODES.SIMPLE

const getPreferredViewMode = () =>
  localStorage.getItem(HARDWARE_VIEW_MODE_STORAGE_KEY) === HARDWARE_VIEW_MODES.SIMPLE
    ? HARDWARE_VIEW_MODES.SIMPLE
    : HARDWARE_VIEW_MODES.COMPLETE

const setPreferredViewMode = (viewMode) => localStorage.setItem(HARDWARE_VIEW_MODE_STORAGE_KEY, viewMode)

const resolveViewMode = ({ canUseCompleteView, searchParams }) => {
  if (!canUseCompleteView) return HARDWARE_VIEW_MODES.SIMPLE

  const requestedViewMode = searchParams.get('view')
  if (requestedViewMode !== null) return normalizeViewMode(requestedViewMode)

  return getPreferredViewMode()
}

export const useHardwareViewMode = () => {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [user] = useUser()
  const [pendingPreference, setPendingPreference] = useState(null)
  const canUseCompleteView = canUseCompleteHardwareView(user)
  const viewMode = resolveViewMode({ canUseCompleteView, searchParams })

  const navigateToViewMode = (nextMode) => {
    if (!canUseCompleteView) return
    const params = new URLSearchParams(searchParams)
    params.set('view', normalizeViewMode(nextMode))
    navigate({
      pathname: location.pathname,
      search: `?${params.toString()}`
    })
  }

  const buildSearch = (extraParams = {}) => {
    const params = new URLSearchParams(searchParams)
    Object.entries(extraParams).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        params.delete(key)
      } else {
        params.set(key, value)
      }
    })
    return `?${params.toString()}`
  }

  const selectViewMode = (nextMode) => {
    if (!canUseCompleteView || nextMode === viewMode) return

    navigateToViewMode(nextMode)
    setPendingPreference(nextMode)
  }

  const resolveViewPreference = (shouldSave) => {
    if (shouldSave && pendingPreference) setPreferredViewMode(pendingPreference)
    setPendingPreference(null)
  }

  return {
    viewMode,
    selectViewMode,
    buildSearch,
    canUseCompleteView,
    viewPreference: pendingPreference ? { viewMode: pendingPreference, onDecision: resolveViewPreference } : null
  }
}
